import { BrowserRouter as Router, Routes, Route, Link, NavLink, useNavigate, useParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { getAccount, loginAccount, logoutAccount, registerAccount } from "./accountApi";
import { addChild, getActiveChild, getChildren, setActiveChild, updateChild } from "./childStorage";
import { ALLERGENS } from "./allergenData";
import { loadAccountState, saveCurrentStateToAccount, STATE_CHANGED_EVENT } from "./storage";
import {
  FOOD_PRACTICE_FOODS,
  classifyFood,
  getFoodMayContain,
  getFoodUsuallyContains,
  normalizeAllergenId,
  STATES,
} from "./foodLogic";

const DEFAULT_EMERGENCY_STEPS = [
  "If breathing is hard, the lips, tongue, or throat are swelling, the child faints, or symptoms involve more than one body area, use epinephrine if available and call 911 now.",
  "If epinephrine is not available and symptoms seem serious, call 911 now.",
  "After emergency help is on the way, call the child's emergency contact.",
];
const SETUP_DRAFT_KEY = "safeplate-setup-draft";
const LAST_SAVE_MESSAGE_KEY = "safeplate-last-save-message";

function getAllergenName(id) {
  const normalizedId = normalizeAllergenId(id);
  return ALLERGENS.find((a) => a.id === normalizedId)?.name || id;
}

function getAllergenNames(child) {
  return (child?.allergens || []).map(getAllergenName);
}

function getChildById(id) {
  return getChildren().find((child) => child.id === id) || null;
}

function formatTimestamp(value) {
  if (!value) return "Not saved yet";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not saved yet";
  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function cleanPhoneNumber(phone) {
  return String(phone || "").replace(/[^\d+]/g, "");
}

function getProfileReadiness(child) {
  if (!child) {
    return {
      complete: false,
      missing: ["Create a child profile"],
    };
  }

  const missing = [];
  if (!child.allergens?.length) missing.push("Foods to avoid");
  if (!getEmergencyInstructions(child)) missing.push("Family or doctor emergency instructions");
  if (child.hasEpiPen && !child.epiPenLocation) missing.push("Medicine location");
  if (!child.emergencyContacts?.length) missing.push("Emergency contact");

  return {
    complete: missing.length === 0,
    missing,
  };
}

function readSetupDraft() {
  try {
    const draft = JSON.parse(localStorage.getItem(SETUP_DRAFT_KEY) || "null");
    return draft && typeof draft === "object" ? draft : null;
  } catch {
    return null;
  }
}

function writeSetupDraft(draft) {
  try {
    localStorage.setItem(
      SETUP_DRAFT_KEY,
      JSON.stringify({ ...draft, savedAt: new Date().toISOString() }),
    );
  } catch {
    // Draft saving is helpful, but the form should still work if browser storage is blocked.
  }
}

function clearSetupDraft() {
  try {
    localStorage.removeItem(SETUP_DRAFT_KEY);
  } catch {
    // The saved profile is more important than clearing a temporary draft marker.
  }
}

function readAndClearSaveMessage() {
  try {
    const message = sessionStorage.getItem(LAST_SAVE_MESSAGE_KEY) || "";
    sessionStorage.removeItem(LAST_SAVE_MESSAGE_KEY);
    return message;
  } catch {
    return "";
  }
}

function rememberSaveMessage(message) {
  try {
    sessionStorage.setItem(LAST_SAVE_MESSAGE_KEY, message);
  } catch {
    // Ignore session storage failures; the saved profile still opens.
  }
}

function useActiveChildState() {
  const [version, setVersion] = useState(0);
  const child = useMemo(() => {
    void version;
    return getActiveChild();
  }, [version]);

  useEffect(() => {
    function refreshFromStorage() {
      setVersion((current) => current + 1);
    }

    window.addEventListener(STATE_CHANGED_EVENT, refreshFromStorage);
    return () => window.removeEventListener(STATE_CHANGED_EVENT, refreshFromStorage);
  }, []);

  return {
    child,
    refresh: () => setVersion((current) => current + 1),
  };
}

function saveFoodPractice(foodId, result) {
  const key = "safeplate-food-practice";
  let current = {};
  try {
    current = JSON.parse(localStorage.getItem(key) || "{}");
  } catch {
    current = {};
  }

  const item = current[foodId] || { tries: 0, remembered: 0 };
  current[foodId] = {
    tries: item.tries + 1,
    remembered: item.remembered + (result.correct ? 1 : 0),
    lastChoice: result.choice,
    safestChoice: result.safestChoice,
    lastSeenAt: new Date().toISOString(),
  };
  localStorage.setItem(key, JSON.stringify(current));
}

function getEmergencyInstructions(child) {
  const written =
    child?.emergencyInstructions ||
    child?.profile?.medicalInfo?.emergencyInstructions ||
    "";
  return String(written).trim();
}

function buildSafetySummary(child) {
  if (!child) return "";

  const allergenNames = getAllergenNames(child);
  const contacts = child.emergencyContacts || [];
  const emergencyInstructions = getEmergencyInstructions(child);
  const lines = [
    `SafePlate safety profile for ${child.name}${child.age ? `, age ${child.age}` : ""}`,
    `Last updated: ${formatTimestamp(child.updatedAt)}`,
    "",
    "What to do first:",
    "- If symptoms seem serious, use epinephrine if available and call 911 now.",
    "- Follow the family or doctor's written plan.",
  ];

  if (emergencyInstructions) {
    lines.push(`- ${emergencyInstructions}`);
  } else {
    DEFAULT_EMERGENCY_STEPS.forEach((step) => lines.push(`- ${step}`));
  }

  if (child.profile?.safetySettings?.crossContactSensitivity) {
    lines.push("- Use clean hands, clean utensils, and a clean surface before preparing food.");
  }

  lines.push(
    "",
    "Do not give these foods:",
  );

  if (allergenNames.length) {
    allergenNames.forEach((name) => lines.push(`- ${name}`));
  } else {
    lines.push("- No foods are listed yet. Ask the parent or guardian before giving food.");
  }

  lines.push("", "Medicine is here:");
  if (child.hasEpiPen) {
    lines.push(`- ${child.epiPenLocation || "Epinephrine is available, but the location has not been added yet."}`);
  } else {
    lines.push("- No epinephrine medicine has been listed in this profile.");
  }

  if (child.profile?.medicalInfo?.hasAsthma) {
    lines.push("- Breathing problems are an extra concern for this child.");
  }

  lines.push("", "Call these people:");
  if (contacts.length) {
    contacts.forEach((contact) => {
      lines.push(`- ${[contact.name, contact.relation, contact.phone].filter(Boolean).join(" - ")}`);
    });
  } else {
    lines.push("- No emergency contacts are listed. Ask the parent or guardian who to call.");
  }

  if (child.parentNotes) {
    lines.push("", "Notes from the family:", child.parentNotes);
  }

  return lines.join("\n");
}

async function copyText(text) {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      // Fall back to the older copy method below.
    }
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.top = "-1000px";
  document.body.appendChild(textarea);
  textarea.select();
  try {
    const copied = document.execCommand("copy");
    if (!copied) throw new Error("Copy command failed");
  } finally {
    document.body.removeChild(textarea);
  }
}

function SafetyOnlyHeader() {
  return (
    <header className="app-header app-header--safety">
      <Link className="brand-link" to="/">
        <span className="brand-dot" aria-hidden="true" />
        <span>SafePlate safety profile</span>
      </Link>
    </header>
  );
}

function AppHeader() {
  const child = getActiveChild();

  return (
    <header className="app-header">
      <Link className="brand-link" to="/">
        <img className="brand-mark" src="/SafePlate_trimmed.png" alt="" />
        <span>SafePlate</span>
      </Link>
      {child ? <p className="active-profile">Profile: {child.name}</p> : null}
      <nav className="app-nav" aria-label="Main navigation">
        <NavLink to="/" end>Home</NavLink>
        <NavLink to="/safety">Safety profile</NavLink>
        <NavLink to="/account">Account</NavLink>
      </nav>
    </header>
  );
}

function Page({ children, safety = false }) {
  return (
    <div className={safety ? "app-shell app-shell--safety" : "app-shell"}>
      <a className="skip-link" href="#main-content">Skip to content</a>
      {safety ? <SafetyOnlyHeader /> : <AppHeader />}
      <main id="main-content" className={safety ? "page page--safety" : "page"}>{children}</main>
      <footer className="app-footer">
        <p>
          Website proudly built by{" "}
          <a href="https://baumbachsolutions.com">Baumbach Solutions</a>
        </p>
      </footer>
    </div>
  );
}

function ProfileReadiness({ child }) {
  const readiness = getProfileReadiness(child);

  return (
    <section className={readiness.complete ? "readiness-panel readiness-panel--ready" : "readiness-panel"} aria-labelledby="readiness-heading">
      <h2 id="readiness-heading">{readiness.complete ? "Profile ready" : "Profile needs attention"}</h2>
      {readiness.complete ? (
        <p>The main safety details are filled in.</p>
      ) : (
        <>
          <p>Add these before relying on the profile for a caregiver handoff:</p>
          <ul>
            {readiness.missing.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </>
      )}
      {child?.updatedAt ? <p>Last updated: {formatTimestamp(child.updatedAt)}</p> : null}
    </section>
  );
}

function LaunchScreen() {
  const { child } = useActiveChildState();

  return (
    <Page>
      <section className="hero-panel">
        <h1>SafePlate</h1>
        <p>Food allergy practice for children, with a fast safety profile for the adults caring for them.</p>
        {child ? (
          <p>
            Current profile: {child.name}
            {child.age ? `, age ${child.age}` : ""}
          </p>
        ) : (
          <p>No child profile is set up yet.</p>
        )}
      </section>

      {child ? <ProfileReadiness child={child} /> : null}

      <section className="action-list-panel" aria-labelledby="start-heading">
        <h2 id="start-heading">What do you need?</h2>
        <ul className="action-list">
          <li>
            <Link className="button button--primary" to="/safety">Show safety profile</Link>
            <p>Use this when another adult needs to know what foods to avoid and what to do in an emergency.</p>
          </li>
          <li>
            <Link className="button button--secondary" to="/adult/setup">Set up or update profile</Link>
            <p>Add the child's foods to avoid, medicine location, emergency steps, contacts, and notes.</p>
          </li>
          <li>
            <Link className="button button--secondary" to="/learn">Practice food choices</Link>
            <p>Help the child practice whether a food is safe, not safe, or something to ask about first.</p>
          </li>
        </ul>
      </section>

      <section className="status-panel" aria-labelledby="device-note-heading">
        <h2 id="device-note-heading">Before you rely on it</h2>
        <p>Create an account to keep this profile available on other devices. For a quick handoff, copy or print the safety profile.</p>
        <p>
          <Link className="text-link" to="/account">Set up account access</Link>
        </p>
      </section>
    </Page>
  );
}

function AccountPage() {
  const [mode, setMode] = useState("register");
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [familyName, setFamilyName] = useState("");
  const [status, setStatus] = useState("Checking account...");
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    getAccount().then(async (account) => {
      if (!active) return;
      setUser(account);
      setStatus(account ? "Signed in. Profiles sync with this family account." : "No account is signed in on this device.");
      if (account) await loadAccountState();
    });
    return () => {
      active = false;
    };
  }, []);

  async function submitAccount(event) {
    event.preventDefault();
    setError("");
    setStatus(mode === "register" ? "Creating account..." : "Signing in...");

    try {
      const account =
        mode === "register"
          ? await registerAccount({ email, password, displayName, familyName })
          : await loginAccount({ email, password });
      setUser(account);
      setStatus(
        account.localProfileBackedUp
          ? "Signed in. The account profile is showing. This device had a different local profile, so SafePlate kept a local backup before switching."
          : "Signed in. This device is synced with the family account.",
      );
      setPassword("");
    } catch (accountError) {
      setError(accountError.message);
      setStatus("Account was not updated.");
    }
  }

  async function syncNow() {
    setError("");
    setStatus("Saving this device's profile to the family account...");
    try {
      await saveCurrentStateToAccount();
      setStatus("Family account is up to date.");
    } catch (syncError) {
      setError(syncError.message);
      setStatus("Sync did not finish.");
    }
  }

  async function signOut() {
    setError("");
    setStatus("Signing out...");
    try {
      await logoutAccount();
      setUser(null);
      setStatus("Signed out. The profile on this device is still here.");
    } catch (logoutError) {
      setError(logoutError.message);
      setStatus("Could not sign out.");
    }
  }

  return (
    <Page>
      <section className="hero-panel hero-panel--compact">
        <h1>Account access</h1>
        <p>Use an account when this family needs the same SafePlate profiles on more than one device.</p>
        <p className="save-status">{status}</p>
        <p>If this device already has a child profile and the account is empty, SafePlate saves that profile into the account after sign-in.</p>
        {error ? <p className="alert-text" role="alert">{error}</p> : null}
      </section>

      {user ? (
        <section className="form-panel" aria-labelledby="signed-in-heading">
          <h2 id="signed-in-heading">Signed in</h2>
          <p>{user.email}</p>
          {user.family ? <p>Family account: {user.family.name}</p> : null}
          <p className="button-row">
            <button className="button button--primary" type="button" onClick={syncNow}>Sync this device now</button>
            <button className="button button--secondary" type="button" onClick={signOut}>Sign out</button>
          </p>
          <p>
            <Link className="text-link" to="/adult/setup">Update child profile</Link>
          </p>
        </section>
      ) : (
        <section className="form-panel" aria-labelledby="account-form-heading">
          <h2 id="account-form-heading">{mode === "register" ? "Create family account" : "Sign in"}</h2>
          <form className="field-stack" onSubmit={submitAccount}>
            {mode === "register" ? (
              <>
                <p className="field">
                  <label>
                    Adult name{" "}
                    <input value={displayName} onChange={(event) => setDisplayName(event.target.value)} autoComplete="name" />
                  </label>
                </p>
                <p className="field">
                  <label>
                    Family name{" "}
                    <input value={familyName} onChange={(event) => setFamilyName(event.target.value)} autoComplete="organization" />
                  </label>
                </p>
              </>
            ) : null}
            <p className="field">
              <label>
                Email{" "}
                <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" required />
              </label>
            </p>
            <p className="field">
              <label>
                Password{" "}
                <input
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete={mode === "register" ? "new-password" : "current-password"}
                  minLength="8"
                  required
                />
              </label>
            </p>
            <p className="button-row">
              <button className="button button--primary" type="submit">{mode === "register" ? "Create account" : "Sign in"}</button>
            </p>
          </form>
          <p className="button-row">
            {mode === "register" ? (
              <button className="button button--secondary" type="button" onClick={() => setMode("login")}>I already have an account</button>
            ) : (
              <button className="button button--secondary" type="button" onClick={() => setMode("register")}>Create a family account</button>
            )}
          </p>
        </section>
      )}
    </Page>
  );
}

function AccountBootstrap({ children }) {
  useEffect(() => {
    let active = true;
    getAccount().then(async (account) => {
      if (active && account) await loadAccountState();
    });
    return () => {
      active = false;
    };
  }, []);

  return children;
}

function PracticeHome() {
  const { child } = useActiveChildState();
  const allergenNames = getAllergenNames(child);

  return (
    <Page>
      <section className="hero-panel hero-panel--practice">
        <h1>Practice food choices</h1>
        {child ? (
          <>
            <p>Today, {child.name} can practice deciding what to do before eating.</p>
            <p>
              Foods to avoid:{" "}
              {allergenNames.length ? allergenNames.join(", ") : "none listed yet. Ask an adult to finish the profile first."}
            </p>
            <p>These are practice examples. Real food still needs a package label check or an adult who knows the recipe.</p>
          </>
        ) : (
          <p>An adult needs to set up a child profile before practice starts.</p>
        )}
      </section>

      <section className="practice-panel" aria-labelledby="practice-heading">
        <h2 id="practice-heading">Start here</h2>
        <article className="practice-card">
          <h3>Safe, ask first, or not safe</h3>
          <p>Look at one food and choose what to do before eating it.</p>
          <Link className="button button--primary" to={child ? "/learn/food-sort" : "/adult/setup"}>
            {child ? "Start practice" : "Set up profile"}
          </Link>
        </article>
      </section>
    </Page>
  );
}

function FoodSort() {
  const navigate = useNavigate();
  const { child } = useActiveChildState();
  const [index, setIndex] = useState(0);
  const [result, setResult] = useState(null);
  const food = FOOD_PRACTICE_FOODS[index];

  if (!child) {
    return (
      <Page>
        <section className="hero-panel hero-panel--practice">
          <h1>Practice food choices</h1>
          <p>An adult needs to set up a child profile first.</p>
          <Link className="button button--primary" to="/adult/setup">Set up profile</Link>
        </section>
      </Page>
    );
  }

  if (!food) {
    return (
      <Page>
        <section className="practice-panel">
          <h1>Practice finished</h1>
          <p>You went through every food in this set.</p>
          <p className="button-row">
            <button className="button button--primary" type="button" onClick={() => { setIndex(0); setResult(null); }}>
              Practice again
            </button>
            <button className="button button--secondary" type="button" onClick={() => navigate("/learn")}>
              Back to practice
            </button>
          </p>
        </section>
      </Page>
    );
  }

  const safestChoice = classifyFood(food, child.allergens || []);

  function explain(choice) {
    const avoidList = new Set((child.allergens || []).map(normalizeAllergenId));
    const usuallyContains = getFoodUsuallyContains(food).filter((id) => avoidList.has(id));
    const mayContain = getFoodMayContain(food).filter((id) => avoidList.has(id));

    if (safestChoice === STATES.UNSAFE) {
      return `This practice food usually has ${usuallyContains.map(getAllergenName).join(", ")}. ${child.name} should not eat this example. In real life, check the label or ask an adult because recipes can change.`;
    }
    if (safestChoice === STATES.ASK) {
      return `This practice food can have ${mayContain.map(getAllergenName).join(", ")}. ${child.name} should ask an adult to check the label or recipe first.`;
    }
    if (choice === STATES.SAFE) {
      return `This practice food does not list foods from ${child.name}'s avoid list. Real food can be different, so an adult should still check.`;
    }
    return `This practice food does not list foods from ${child.name}'s avoid list, but asking an adult is always okay.`;
  }

  function choose(choice) {
    const nextResult = {
      choice,
      safestChoice,
      correct: choice === safestChoice,
      explanation: explain(choice),
    };
    saveFoodPractice(food.id, nextResult);
    setResult(nextResult);
  }

  function nextFood() {
    setIndex((current) => current + 1);
    setResult(null);
  }

  return (
    <Page>
      <section className="hero-panel hero-panel--practice">
        <h1>Safe, ask first, or not safe</h1>
        <p className="eyebrow">
          Food {index + 1} of {FOOD_PRACTICE_FOODS.length}
        </p>
        <p>This is practice only. Real food still needs an adult or label check.</p>
      </section>

      <section className="practice-panel" aria-labelledby="food-heading">
        <h2 id="food-heading">{food.label}</h2>
        <p>What should {child.name} do before eating this?</p>
        <div className="choice-grid">
          <button className="button button--choice" type="button" onClick={() => choose(STATES.SAFE)}>
            Safe for me
          </button>
          <button className="button button--choice button--caution" type="button" onClick={() => choose(STATES.ASK)}>
            Ask first
          </button>
          <button className="button button--choice button--danger" type="button" onClick={() => choose(STATES.UNSAFE)}>
            Not safe for me
          </button>
        </div>
      </section>

      {result ? (
        <section className={result.correct ? "status-panel status-panel--success" : "status-panel status-panel--caution"} aria-live="polite" aria-labelledby="answer-heading">
          <h2 id="answer-heading">{result.correct ? "Good choice" : "Remember this food"}</h2>
          <p>{result.explanation}</p>
          <button className="button button--primary" type="button" onClick={nextFood}>
            Next food
          </button>
        </section>
      ) : null}
    </Page>
  );
}

function SafetyProfile() {
  const { childId } = useParams();
  const child = childId ? getChildById(childId) : getActiveChild();
  const allergenNames = getAllergenNames(child);
  const contacts = child?.emergencyContacts || [];
  const emergencyInstructions = getEmergencyInstructions(child);
  const [saveStatus] = useState(readAndClearSaveMessage);
  const [copyStatus, setCopyStatus] = useState("");
  const [shareStatus, setShareStatus] = useState("");
  const [shareLink, setShareLink] = useState("");

  async function copySafetySummary() {
    if (!child) return;

    try {
      await copyText(buildSafetySummary(child));
      setCopyStatus("Safety summary copied.");
    } catch {
      setCopyStatus("Copy did not work. Use print instead.");
    }
  }

  function printSafetyProfile() {
    window.print();
  }

  async function createCaregiverLink() {
    if (!child) return;
    setShareStatus("Creating caregiver link...");
    setShareLink("");

    try {
      const response = await fetch("/api/share-links", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ childId: child.id, label: "Caregiver safety link" }),
      });
      const contentType = response.headers.get("content-type") || "";
      if (!contentType.includes("application/json")) throw new Error("Account server is not available on this preview.");
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "SafePlate could not create that link.");
      const nextLink = new URL(data.path, window.location.origin).toString();
      setShareLink(nextLink);
      await copyText(nextLink);
      setShareStatus("Caregiver link copied.");
    } catch (linkError) {
      setShareStatus(linkError.message);
    }
  }

  return (
    <Page safety>
      <section className="safety-card safety-card--hero">
        <h1>{child ? `${child.name}'s safety profile` : "Safety profile"}</h1>
        {child ? (
          <>
            <p>Show this page to any adult who may feed or supervise {child.name}.</p>
            <p className="eyebrow">Last updated: {formatTimestamp(child.updatedAt)}</p>
            {saveStatus ? <p className="save-status" aria-live="polite">{saveStatus}</p> : null}
          </>
        ) : (
          <p>No child profile is selected. Set up a profile so caregivers know what to avoid.</p>
        )}
      </section>

      {child ? (
        <>
          <ProfileReadiness child={child} />

          <section className="safety-card priority-block" aria-labelledby="first-heading">
            <h2 id="first-heading">What to do first</h2>
            <p>If symptoms seem serious, use epinephrine if available and call 911 now.</p>
            <p>Follow the family or doctor's written plan.</p>
            {emergencyInstructions ? (
              <p>{emergencyInstructions}</p>
            ) : (
              <>
                <p>No family-written emergency plan has been added yet. Use this safety reminder until the profile is completed:</p>
                <ol>
                  {DEFAULT_EMERGENCY_STEPS.map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ol>
              </>
            )}
            {child.profile?.safetySettings?.crossContactSensitivity ? (
              <p>Use clean hands, clean utensils, and a clean surface before preparing food.</p>
            ) : null}
          </section>

          <section className="safety-card medicine-block" aria-labelledby="medicine-heading">
            <h2 id="medicine-heading">Medicine is here</h2>
            {child.hasEpiPen ? (
              <p>{child.epiPenLocation || "Epinephrine is available, but the location has not been added yet."}</p>
            ) : (
              <p>No epinephrine medicine has been listed in this profile.</p>
            )}
            {child.profile?.medicalInfo?.hasAsthma ? (
              <p>{child.name} also has asthma. Take breathing symptoms seriously.</p>
            ) : null}
          </section>

          <section className="safety-card avoid-block" aria-labelledby="do-not-give-heading">
            <h2 id="do-not-give-heading">Do not give these foods</h2>
            {allergenNames.length ? (
              <ul className="pill-list pill-list--danger">
                {allergenNames.map((name) => (
                  <li key={name}>{name}</li>
                ))}
              </ul>
            ) : (
              <p>No foods are listed yet. Ask the parent or guardian before giving food.</p>
            )}
          </section>

          <section className="safety-card call-list" aria-labelledby="contacts-heading">
            <h2 id="contacts-heading">Call these people</h2>
            {contacts.length ? (
              <ul>
                {contacts.map((contact, index) => (
                  <li key={`${contact.name}-${index}`}>
                    {[contact.name, contact.relation, contact.phone].filter(Boolean).join(" - ")}
                    {contact.phone ? (
                      <>
                        {" "}
                        <a className="button button--primary button--call" href={`tel:${cleanPhoneNumber(contact.phone)}`}>Call</a>
                      </>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : (
              <p>No emergency contacts are listed. Ask the parent or guardian who to call.</p>
            )}
          </section>

          {child.parentNotes ? (
            <section className="safety-card" aria-labelledby="notes-heading">
              <h2 id="notes-heading">Notes from the family</h2>
              <p>{child.parentNotes}</p>
            </section>
          ) : null}

          <section className="safety-card share-panel" aria-labelledby="share-heading">
            <h2 id="share-heading">Share this information</h2>
            <p>Copy, print, or create a read-only caregiver link. Caregiver links require a signed-in account.</p>
            <p className="button-row">
              <button className="button button--secondary" type="button" onClick={copySafetySummary}>Copy safety summary</button>
              <button className="button button--secondary" type="button" onClick={printSafetyProfile}>Print safety profile</button>
              <button className="button button--primary" type="button" onClick={createCaregiverLink}>Create caregiver link</button>
            </p>
            {copyStatus ? <p className="save-status" aria-live="polite">{copyStatus}</p> : null}
            {shareStatus ? <p className="save-status" aria-live="polite">{shareStatus}</p> : null}
            {shareLink ? <p className="share-link">{shareLink}</p> : null}
            <p>
              <Link className="text-link" to="/adult/setup">Update this profile</Link>
            </p>
          </section>
        </>
      ) : (
        <section className="safety-card">
          <p>Profiles are saved on the device where they are created.</p>
          <Link className="button button--primary" to="/adult/setup">Set up a child profile</Link>
        </section>
      )}
    </Page>
  );
}

function SharedSafetyProfile() {
  const { token } = useParams();
  const [child, setChild] = useState(null);
  const [status, setStatus] = useState("Loading safety profile...");

  useEffect(() => {
    let active = true;

    async function loadSharedProfile() {
      try {
        const response = await fetch(`/api/shared/safety/${token}`, { credentials: "omit" });
        const contentType = response.headers.get("content-type") || "";
        if (!contentType.includes("application/json")) throw new Error("Safety link server is not available on this preview.");
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || "Safety profile was not found.");
        if (!active) return;
        setChild(data.child);
        setStatus("");
      } catch (error) {
        if (!active) return;
        setStatus(error.message);
      }
    }

    loadSharedProfile();
    return () => {
      active = false;
    };
  }, [token]);

  return (
    <Page safety>
      <section className="safety-card safety-card--hero">
        <h1>{child ? `${child.name}'s safety profile` : "Safety profile"}</h1>
        {status ? <p>{status}</p> : <p>Show this page to any adult who may feed or supervise {child.name}.</p>}
      </section>

      {child ? (
        <>
          <section className="safety-card priority-block" aria-labelledby="shared-first-heading">
            <h2 id="shared-first-heading">What to do first</h2>
            <p>If symptoms seem serious, use epinephrine if available and call 911 now.</p>
            <p>Follow the family or doctor's written plan.</p>
            {getEmergencyInstructions(child) ? (
              <p>{getEmergencyInstructions(child)}</p>
            ) : (
              <ol>
                {DEFAULT_EMERGENCY_STEPS.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
            )}
            {child.profile?.safetySettings?.crossContactSensitivity ? (
              <p>Use clean hands, clean utensils, and a clean surface before preparing food.</p>
            ) : null}
          </section>

          <section className="safety-card medicine-block" aria-labelledby="shared-medicine-heading">
            <h2 id="shared-medicine-heading">Medicine is here</h2>
            {child.hasEpiPen ? (
              <p>{child.epiPenLocation || "Epinephrine is available, but the location has not been added yet."}</p>
            ) : (
              <p>No epinephrine medicine has been listed in this profile.</p>
            )}
            {child.profile?.medicalInfo?.hasAsthma ? (
              <p>{child.name} also has asthma. Take breathing symptoms seriously.</p>
            ) : null}
          </section>

          <section className="safety-card avoid-block" aria-labelledby="shared-avoid-heading">
            <h2 id="shared-avoid-heading">Do not give these foods</h2>
            {getAllergenNames(child).length ? (
              <ul className="pill-list pill-list--danger">
                {getAllergenNames(child).map((name) => (
                  <li key={name}>{name}</li>
                ))}
              </ul>
            ) : (
              <p>No foods are listed yet. Ask the parent or guardian before giving food.</p>
            )}
          </section>

          <section className="safety-card call-list" aria-labelledby="shared-contacts-heading">
            <h2 id="shared-contacts-heading">Call these people</h2>
            {child.emergencyContacts?.length ? (
              <ul>
                {child.emergencyContacts.map((contact, index) => (
                  <li key={`${contact.name}-${index}`}>
                    {[contact.name, contact.relation, contact.phone].filter(Boolean).join(" - ")}
                    {contact.phone ? (
                      <>
                        {" "}
                        <a className="button button--primary button--call" href={`tel:${cleanPhoneNumber(contact.phone)}`}>Call</a>
                      </>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : (
              <p>No emergency contacts are listed. Ask the parent or guardian who to call.</p>
            )}
          </section>

          {child.parentNotes ? (
            <section className="safety-card" aria-labelledby="shared-notes-heading">
              <h2 id="shared-notes-heading">Notes from the family</h2>
              <p>{child.parentNotes}</p>
            </section>
          ) : null}

          <p className="eyebrow">Last updated: {formatTimestamp(child.updatedAt)}</p>
          <p className="button-row">
            <button className="button button--secondary" type="button" onClick={() => window.print()}>Print safety profile</button>
          </p>
        </>
      ) : null}
    </Page>
  );
}

function AdultSetup() {
  const navigate = useNavigate();
  const { child, refresh } = useActiveChildState();
  const draft = child ? null : readSetupDraft();
  const [name, setName] = useState(child?.name || draft?.name || "");
  const [age, setAge] = useState(child?.age || draft?.age || "");
  const [allergens, setAllergens] = useState(new Set(child?.allergens || draft?.allergens || []));
  const [hasEpiPen, setHasEpiPen] = useState(child ? !!child.hasEpiPen : !!draft?.hasEpiPen);
  const [epiPenLocation, setEpiPenLocation] = useState(child?.epiPenLocation || draft?.epiPenLocation || "");
  const [emergencyInstructions, setEmergencyInstructions] = useState(getEmergencyInstructions(child) || draft?.emergencyInstructions || "");
  const [hasAsthma, setHasAsthma] = useState(child ? !!child?.profile?.medicalInfo?.hasAsthma : !!draft?.hasAsthma);
  const [crossContact, setCrossContact] = useState(child ? !!child?.profile?.safetySettings?.crossContactSensitivity : !!draft?.crossContact);
  const [contactName, setContactName] = useState(child?.emergencyContacts?.[0]?.name || draft?.contactName || "");
  const [contactRelation, setContactRelation] = useState(child?.emergencyContacts?.[0]?.relation || draft?.contactRelation || "");
  const [contactPhone, setContactPhone] = useState(child?.emergencyContacts?.[0]?.phone || draft?.contactPhone || "");
  const [notes, setNotes] = useState(child?.parentNotes || draft?.notes || "");
  const [draftStatus, setDraftStatus] = useState(draft ? `Draft restored from ${formatTimestamp(draft.savedAt)}.` : "");
  const [saveStatus, setSaveStatus] = useState("");

  useEffect(() => {
    if (child) return undefined;

    const timer = window.setTimeout(() => {
      const nextDraft = {
        name,
        age,
        allergens: Array.from(allergens),
        hasEpiPen,
        epiPenLocation,
        emergencyInstructions,
        hasAsthma,
        crossContact,
        contactName,
        contactRelation,
        contactPhone,
        notes,
      };
      const hasDraftContent = Object.values(nextDraft).some((value) => {
        if (Array.isArray(value)) return value.length > 0;
        if (typeof value === "boolean") return value;
        return String(value || "").trim();
      });

      if (hasDraftContent) {
        writeSetupDraft(nextDraft);
        setDraftStatus("Draft saved on this device.");
      }
    }, 800);

    return () => window.clearTimeout(timer);
  }, [
    age,
    allergens,
    child,
    contactName,
    contactPhone,
    contactRelation,
    crossContact,
    emergencyInstructions,
    epiPenLocation,
    hasAsthma,
    hasEpiPen,
    name,
    notes,
  ]);

  function toggleAllergen(id) {
    const next = new Set(allergens);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setAllergens(next);
  }

  async function saveProfile(event) {
    event.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) {
      setSaveStatus("Add the child's name before saving.");
      return;
    }
    setSaveStatus("Saving profile...");

    const payload = {
      name: trimmedName,
      age: age.trim(),
      avatar: child?.avatar || "SMILE",
      allergens: Array.from(allergens),
      hasEpiPen,
      epiPenLocation: epiPenLocation.trim(),
      emergencyInstructions: emergencyInstructions.trim(),
      emergencyContacts: [
        {
          name: contactName.trim(),
          relation: contactRelation.trim(),
          phone: contactPhone.trim(),
        },
      ].filter((contact) => contact.name || contact.relation || contact.phone),
      parentNotes: notes.trim(),
      profile: {
        medicalInfo: {
          hasAsthma,
          medications: child?.profile?.medicalInfo?.medications || "",
          reactionType: child?.profile?.medicalInfo?.reactionType || "standard",
          emergencyInstructions: emergencyInstructions.trim(),
        },
        safetySettings: {
          crossContactSensitivity: crossContact,
          prefersDedicatedFacility: child?.profile?.safetySettings?.prefersDedicatedFacility || false,
        },
      },
    };

    if (child) {
      updateChild(child.id, payload);
    } else {
      const nextState = addChild(payload);
      setActiveChild(nextState.activeChildId);
    }

    refresh();
    clearSetupDraft();

    try {
      const account = await getAccount();
      if (account) {
        await saveCurrentStateToAccount();
        rememberSaveMessage("Profile saved to the family account.");
      } else {
        rememberSaveMessage("Profile saved on this device. Sign in to sync it across devices.");
      }
    } catch {
      rememberSaveMessage("Profile saved on this device. Account sync did not finish.");
    }

    navigate("/safety");
  }

  return (
    <Page>
      <section className="hero-panel hero-panel--compact">
        <h1>Set up the safety profile</h1>
        <p>Add the information another adult needs before giving this child food.</p>
        <p>Create an account to keep this profile available on other devices. After saving, copy or print the safety profile for a quick caregiver handoff.</p>
        {draftStatus ? <p className="save-status" aria-live="polite">{draftStatus}</p> : null}
        {saveStatus ? <p className="save-status" aria-live="polite">{saveStatus}</p> : null}
      </section>

      <form className="setup-form" onSubmit={saveProfile}>
        <section className="form-panel" aria-labelledby="basics-heading">
          <h2 id="basics-heading">1. Child information</h2>
          <p className="field">
            <label>
              Child name{" "}
              <input value={name} onChange={(event) => setName(event.target.value)} required />
            </label>
          </p>
          <p className="field">
            <label>
              Age{" "}
              <input inputMode="numeric" value={age} onChange={(event) => setAge(event.target.value)} />
            </label>
          </p>
        </section>

        <section className="form-panel" aria-labelledby="avoid-heading">
          <h2 id="avoid-heading">2. Foods to avoid</h2>
          <p>Choose anything this child should not eat or should have checked by an adult first.</p>
          <div className="checkbox-grid">
          {ALLERGENS.map((allergen) => (
            <p key={allergen.id} className="check-field">
              <label>
                <input
                  type="checkbox"
                  checked={allergens.has(allergen.id)}
                  onChange={() => toggleAllergen(allergen.id)}
                />{" "}
                {allergen.name}
              </label>
            </p>
          ))}
          </div>
        </section>

        <section className="form-panel" aria-labelledby="medicine-setup-heading">
          <h2 id="medicine-setup-heading">3. Medicine and emergency instructions</h2>
          <p className="field">
            <label>
              Family or doctor emergency instructions{" "}
              <textarea
                rows="4"
                value={emergencyInstructions}
                onChange={(event) => setEmergencyInstructions(event.target.value)}
                placeholder="Example: Follow the written allergy plan. If there is trouble breathing, swelling, faintness, or symptoms in more than one body area, use epinephrine if available and call 911."
              />
            </label>
          </p>
          <p>Copy this from the family's written allergy plan or the doctor's instructions. SafePlate only displays what you add here.</p>
          <p className="check-field">
            <label>
              <input type="checkbox" checked={hasAsthma} onChange={(event) => setHasAsthma(event.target.checked)} />{" "}
              Breathing problems are an extra concern for this child
            </label>
          </p>
          <p className="check-field">
            <label>
              <input type="checkbox" checked={crossContact} onChange={(event) => setCrossContact(event.target.checked)} />{" "}
              Food must be prepared with clean hands, clean utensils, and a clean surface
            </label>
          </p>
          <p className="check-field">
            <label>
              <input type="checkbox" checked={hasEpiPen} onChange={(event) => setHasEpiPen(event.target.checked)} />{" "}
              Epinephrine medicine is available
            </label>
          </p>
          {hasEpiPen ? (
            <p className="field">
              <label>
                Where is the medicine kept?{" "}
                <input value={epiPenLocation} onChange={(event) => setEpiPenLocation(event.target.value)} />
              </label>
            </p>
          ) : null}
        </section>

        <section className="form-panel" aria-labelledby="contact-heading">
          <h2 id="contact-heading">4. Who to call</h2>
          <p className="field">
            <label>
              Contact name{" "}
              <input value={contactName} onChange={(event) => setContactName(event.target.value)} />
            </label>
          </p>
          <p className="field">
            <label>
              Relationship to the child{" "}
              <input value={contactRelation} onChange={(event) => setContactRelation(event.target.value)} />
            </label>
          </p>
          <p className="field">
            <label>
              Phone number{" "}
              <input type="tel" autoComplete="tel" value={contactPhone} onChange={(event) => setContactPhone(event.target.value)} />
            </label>
          </p>
        </section>

        <section className="form-panel" aria-labelledby="notes-setup-heading">
          <h2 id="notes-setup-heading">5. Notes for caregivers</h2>
          <p className="field">
            <label>
              Anything else a caregiver should know{" "}
              <textarea rows="4" value={notes} onChange={(event) => setNotes(event.target.value)} />
            </label>
          </p>
        </section>

        <p className="form-actions">
          <button className="button button--primary button--wide" type="submit">Save and show safety profile</button>
        </p>
      </form>
    </Page>
  );
}

function App() {
  return (
    <Router>
      <AccountBootstrap>
        <Routes>
          <Route path="/" element={<LaunchScreen />} />
          <Route path="/learn" element={<PracticeHome />} />
          <Route path="/learn/food-sort" element={<FoodSort />} />
          <Route path="/safety" element={<SafetyProfile />} />
          <Route path="/safety/:childId" element={<SafetyProfile />} />
          <Route path="/shared/safety/:token" element={<SharedSafetyProfile />} />
          <Route path="/adult/setup" element={<AdultSetup />} />
          <Route path="/account" element={<AccountPage />} />
          <Route path="*" element={<LaunchScreen />} />
        </Routes>
      </AccountBootstrap>
    </Router>
  );
}

export default App;
